import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FaCalendarAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import PropTypes from 'prop-types';

const CustomCalendar = ({ 
  value, 
  onChange, 
  placeholder = "mm/dd/yyyy",
  minDate,
  maxDate,
  disabled = false,
  invalid = false,
  ...props 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null);
  const [lastClickTime, setLastClickTime] = useState(0);
  const calendarRef = useRef(null);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update selected date when value prop changes
  useEffect(() => {
    if (value) {
      setSelectedDate(new Date(value));
      setCurrentDate(new Date(value));
    }
  }, [value]);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  const formatDate = (date) => {
    if (!date) return '';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handleInputClick = () => {
    if (!disabled) {
      const now = Date.now();
      const timeDiff = now - lastClickTime;
      
      // Double click detection (within 300ms) and calendar is already open
      if (timeDiff < 300 && isOpen) {
        // Double click while calendar is open - select today's date
        const today = new Date();
        setSelectedDate(today);
        setIsOpen(false);
        if (onChange) {
          onChange(formatDate(today));
        }
      } else {
        // Single click or first click - toggle calendar
        setIsOpen(!isOpen);
      }
      
      setLastClickTime(now);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setIsOpen(false);
    if (onChange) {
      onChange(formatDate(date));
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const isDateDisabled = (date) => {
    date.setHours(12, 0, 0, 0);
    if (minDate && date < new Date(minDate)) return true;
    if (maxDate && date > new Date(maxDate)) return true;
    return false;
  };

  const isDateSelected = (date) => {
    return selectedDate && 
           date.getDate() === selectedDate.getDate() &&
           date.getMonth() === selectedDate.getMonth() &&
           date.getFullYear() === selectedDate.getFullYear();
  };

  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Debug: log invalid prop
  React.useEffect(() => {
    if (invalid) {
      // Invalid prop received
    }
  }, [invalid]);

  return (
    <CalendarContainer ref={calendarRef} {...props}>
      <InputContainer onClick={handleInputClick} disabled={disabled} invalid={invalid}>
        <DateInput
          value={selectedDate ? formatDate(selectedDate) : ''}
          placeholder={placeholder}
          readOnly
          disabled={disabled}
        />
        <CalendarIcon isOpen={isOpen}>
          <FaCalendarAlt />
        </CalendarIcon>
      </InputContainer>

      {isOpen && (
        <CalendarDropdown>
          <CalendarHeader>
            <NavButton onClick={() => navigateMonth(-1)}>
              <FaChevronLeft />
            </NavButton>
            <MonthYear>
              {months[currentDate.getMonth()]} {currentDate.getFullYear()}
            </MonthYear>
            <NavButton onClick={() => navigateMonth(1)}>
              <FaChevronRight />
            </NavButton>
          </CalendarHeader>

          <WeekDaysHeader>
            {weekDays.map((day) => (
              <WeekDay key={day}>{day}</WeekDay>
            ))}
          </WeekDaysHeader>

          <DaysGrid>
            {getDaysInMonth().map((date, index) => (
              <DayCell
                key={index}
                onClick={() => !isDateDisabled(date) && handleDateSelect(date)}
                isCurrentMonth={isCurrentMonth(date)}
                isSelected={isDateSelected(date)}
                isToday={isToday(date)}
                isDisabled={isDateDisabled(date)}
              >
                {date.getDate()}
              </DayCell>
            ))}
          </DaysGrid>
        </CalendarDropdown>
      )}
    </CalendarContainer>
  );
};

CustomCalendar.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  disabled: PropTypes.bool,
  invalid: PropTypes.bool
};

export default CustomCalendar;

// Styled Components
const CalendarContainer = styled.div`
  position: relative;
  width: fit-content;
  font-family: "Poppins", sans-serif;
  height: 100%;
  width: 100%;
  z-index: 100;
`;

const InputContainer = styled.div`
  display: flex;
  gap: 0.94vw;
  justify-content: center;
  align-items: center;
  background-color: ${props => props.disabled ? '#f5f5f5' : '#F2F3F7'};
  border: ${props => props.invalid ? '2px solid #F02A4C' : '1px solid transparent'};
  border-radius: 25px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  box-shadow: ${props => props.invalid ? '0 0 0 3px rgba(240, 42, 76, 0.1)' : 'none'};
  
  height: 100%;
  width: 100%;

  &:hover {
    background-color: ${props => props.disabled ? '#f5f5f5' : '#E8E9ED'};
  }
`;

const DateInput = styled.input`
  width: 5.52084vw;
  border: none;
  background: transparent;
  font-size: 0.8333vw;
  font-family: "Poppins", sans-serif;
  font-weight: 400;
  color: #1a1a1a;
  opacity: 0.8;
  outline: none;
  cursor: pointer;
  
  &::placeholder {
    color: #B7B7B7;
    letter-spacing: normal;
  }
  
  &:disabled {
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const CalendarIcon = styled.div`
  display: flex;
  color: ${props => props.isOpen ? '#81A0F6' : '#000'};
  pointer-events: none;
  transition: color 0.2s ease;
  
  svg {
    width: 0.885vw;
    height: 1.777vh;
  }
`;

const CalendarDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 1002;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  padding: 12px;
  min-width: 240px;
  width: max-content;
`;

const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const NavButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f3f4f6;
    color: #84A4FC;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const MonthYear = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
`;

const WeekDaysHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 8px;
`;

const WeekDay = styled.div`
  text-align: center;
  font-size: 0.75rem;
  font-weight: 500;
  color: #7A9DFD;
  padding: 8px 4px;
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`;

const DayCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 0.8rem;
  cursor: ${props => props.isDisabled ? 'not-allowed' : 'pointer'};
  color: ${props => {
    if (props.isDisabled) return '#d1d5db';
    if (!props.isCurrentMonth) return '#d1d5db';
    if (props.isSelected) return 'white';
    return '#333';
  }};
  background-color: ${props => {
    if (props.isSelected) return '#7A9DFD';
    if (props.isToday && !props.isSelected) return 'rgba(122, 157, 253, 0.2)';
    return 'transparent';
  }};
  font-weight: ${props => {
    if (props.isSelected || props.isToday) return '600';
    return '400';
  }};
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => {
      if (props.isDisabled) return 'transparent';
      if (props.isSelected) return '#7A9DFD';
      return 'rgba(122, 157, 253, 0.2)';
    }};
    color: ${props => {
      if (props.isDisabled) return '#d1d5db';
      if (props.isSelected) return 'white';
      return '#7A9DFD';
    }};
  }
`;
